import { useState } from 'preact/hooks'
import { RefreshCcw, Plus, AlertCircle, Mail, Star } from 'lucide-preact'
import { useSendingDomains } from '@/hooks/useSendingDomains'
import { invalidateVerifiedDomainsLookup } from '@/hooks/useVerifiedDomainsLookup'
import type { SendingDomainViewDto } from '@/types/sendingDomains'
import { DomainStatusBadge } from './DomainStatusBadge'
import { AddSendingDomainPanel } from './AddSendingDomainPanel'

interface SendingDomainsListProps {
  clientCompanyId: string
}

export function SendingDomainsList({ clientCompanyId }: SendingDomainsListProps) {
  const { domains, loading, error, load, upsert, remove } = useSendingDomains(clientCompanyId)
  const [panelOpen, setPanelOpen] = useState(false)
  const [selected, setSelected] = useState<SendingDomainViewDto | null>(null)

  const openCreate = () => {
    setSelected(null)
    setPanelOpen(true)
  }
  const openDomain = (domain: SendingDomainViewDto) => {
    setSelected(domain)
    setPanelOpen(true)
  }
  const closePanel = () => {
    setPanelOpen(false)
    setSelected(null)
    load()
  }

  const hasDomains = !!(domains && domains.length > 0)

  return (
    <div className="px-6 py-4 max-w-7xl mx-auto space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Sending Domains</h2>
          <p className="text-sm mt-1 text-slate-600 dark:text-slate-400 max-w-prose">
            Authenticate the domains you send marketing email from, then select them on your email activations.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Refreshing' : 'Refresh'}
          </button>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> Add domain
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && !domains && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && !hasDomains && !error && (
        <div className="p-8 border border-dashed rounded-xl border-slate-300 dark:border-slate-600 text-center">
          <Mail className="w-8 h-8 mx-auto text-slate-400" />
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No sending domains yet. Add one to start authenticating.</p>
          <button onClick={openCreate} className="mt-4 inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700">
            <Plus className="w-4 h-4" /> Add domain
          </button>
        </div>
      )}

      {hasDomains && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {domains!.map(domain => (
            <button
              key={domain.Id}
              type="button"
              onClick={() => openDomain(domain)}
              className="text-left rounded-xl border bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-600 p-5 shadow-sm hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium text-slate-900 dark:text-white break-all leading-tight">{domain.Domain}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  {domain.IsDefault && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:text-primary-300">
                      <Star className="w-3 h-3 fill-current" /> Default
                    </span>
                  )}
                  <DomainStatusBadge status={domain.Status} />
                </div>
              </div>
              {domain.Region && <p className="mt-2 text-xs text-slate-400">{domain.Region}</p>}
              <p className="mt-3 text-xs text-primary-600 dark:text-primary-400">Manage →</p>
            </button>
          ))}
        </div>
      )}

      <AddSendingDomainPanel
        isOpen={panelOpen}
        clientCompanyId={clientCompanyId}
        domain={selected}
        onClose={closePanel}
        onSaved={(d) => { upsert(d); invalidateVerifiedDomainsLookup() }}
        onDeleted={(id) => { remove(id); invalidateVerifiedDomainsLookup() }}
      />
    </div>
  )
}
